import { t } from 'i18next';
import { Points, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { isDefined } from '../../../../../../../utils/variables';
import { ActionName, DesignTool, DesignToolDialogState } from '../../../../../design-tool';
import { setToolEditIntersectionToPoint } from '../../utils/point';
import { applyActionInProgress } from '../general';
import { createLine } from '../line/create';
import { createPoint } from './create';

/**
 * Gets the world position that an offset is relative to. In order of priority:
 * the start of the line being created, the hovered point, the only selected point and
 * the position where the cursor was last in the scene.
 */
export const getOffsetOrigin = (
  tool: DesignTool,
  options?: { includeHoveredPoint?: boolean },
): Vector3 | undefined => {
  if (tool.actionInProgress?.actionName === ActionName.CreateLine) {
    return tool.actionInProgress.origin?.clone();
  }

  if (
    (options?.includeHoveredPoint ?? true) &&
    tool.intersection?.object instanceof Points &&
    isDefined(tool.intersection.index)
  ) {
    return tool.asset.designNode.getVertexByIndex(tool.intersection.index, 'world');
  }

  const { points, lineSegments, faces } = tool.currentSelection;

  if (points.length === 1 && lineSegments.length === 0 && faces.length === 0) {
    return tool.asset.designNode.getVertexByPointId(points[0], 'world');
  }

  return tool.currentPoint?.clone();
};

export const openCreatePointByCoordinateDialog = (tool: DesignTool): ContextMenuItem => {
  // Start from where the cursor was last in the scene, which is the point itself when hovering one.
  const cursorPosition = tool.currentPoint?.clone();

  return {
    title: t('contextMenu.create.items.pointByCoordinate', { ns: 'skyviewDesign' }),
    action: () => {
      tool.pointDialogOrigin = cursorPosition;
      tool.uiState = DesignToolDialogState.CreatePointByCoordinate;
      tool.contextMenuCallback?.();
    },
    hotkey: 'K',
  };
};

export const openCreatePointByOffsetDialog = (
  tool: DesignTool,
  options?: { includeHoveredPoint?: boolean },
): ContextMenuItem => {
  const origin = getOffsetOrigin(tool, options);

  return {
    title: t('contextMenu.create.items.pointByOffset', { ns: 'skyviewDesign' }),
    action: () => {
      if (!isDefined(origin)) {
        return;
      }

      tool.pointDialogOrigin = origin;
      tool.uiState = DesignToolDialogState.CreatePointByOffset;
      tool.contextMenuCallback?.();
    },
    disabled: !isDefined(origin),
    hotkey: 'X',
  };
};

/**
 * Distance treshold, if the position is closer than this distance
 * we will use the existing point.
 */
const distanceTreshold = 0.001;

/**
 * Creates a point at the world position. While creating a line, the line continues to the point.
 *
 * An existing point at the position is used as if it was hovered, so that it is not duplicated.
 * Nothing is created at the start of the line being created, since the line would have no length.
 */
export const createPointAtPosition = async (tool: DesignTool, position: Vector3) => {
  const creatingLine = tool.actionInProgress?.actionName === ActionName.CreateLine;
  const lineOrigin = creatingLine ? tool.actionInProgress?.origin : undefined;

  if (isDefined(lineOrigin) && lineOrigin.distanceTo(position) < distanceTreshold) {
    return;
  }

  const localPosition = tool.asset.designNode.getLocalPosition(position);
  const existingPoint = tool.asset.designNode.pointNodes.find(
    (point) => point.vertex.distanceTo(localPosition) < distanceTreshold,
  );

  if (isDefined(existingPoint)) {
    setToolEditIntersectionToPoint(tool, existingPoint.id);
  } else {
    tool.currentPoint = position.clone();
    tool.intersection = undefined;
  }

  if (creatingLine) {
    await applyActionInProgress(tool).action();
    // Continue creating the line from the new point, the same way as when clicking.
    await createLine(tool).action();
    return;
  }

  await createPoint(tool).action();
};

export const createFromDialogFunctions = {
  openCreatePointByCoordinateDialog,
  openCreatePointByOffsetDialog,
};
